import {
  Auth0Provider,
  useAuth0,
  withAuthenticationRequired,
} from "@auth0/auth0-react";
import React, { useEffect } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouteObject,
  useLocation,
} from "react-router-dom";
import { LoadingNewDocs } from "src/components/Assistant/LoadingNewDocs";
import { ROUTE_SLUGS } from "src/config/navigation/routes";
import { Assistant } from "src/pages/assistant";
import { DU_Configure } from "src/pages/assistant/DocumentUpdate/DU_Configure";
import { DU_ListOfDocsToChange } from "src/pages/assistant/DocumentUpdate/DU_ListOfDocsToChange";
import { DU_MultiStepForm } from "src/pages/assistant/DocumentUpdate/DU_MultiStepForm";
import { DU_UpdateDocs } from "src/pages/assistant/DocumentUpdate/DU_UpdateDocs";
import { DeactivatedUserError } from "src/pages/DeactivatedUserError";
import { NavigateToHomeOrReturnTo } from "src/pages/NavigateToHomeOrReturnTo";
import { authHelper } from "src/stores/helpers";
import { MainLayout, MultiStepForm } from "../components";
import config, { ROUTES } from "../config";
import {
  AuthPage,
  CheckoutSuccess,
  Dashboard,
  ErrorPage,
  HelpCenter,
  HelpCenterArticle,
  Loading,
  Organization,
  Roadmap,
} from "../pages";
import QMS from "../pages/QMS";

type ProtectedRouteProps = {
  component: any;
  [key: string]: any;
};

export const ProtectedRoute = ({ component, ...args }: ProtectedRouteProps) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  useEffect(() => {
    if (isAuthenticated) {
      authHelper.setAccessTokenSilently(getAccessTokenSilently);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  const Component = withAuthenticationRequired(component, {
    ...args,
    onRedirecting: () => <Loading />,
  });

  return <Component />;
};

const GENERAL_ROUTES: RouteObject[] = [
  {
    path: ROUTES.MANAGE_ORGANIZATION,
    element: <Organization />,
  },
  {
    path: "",
    element: <NavigateToHomeOrReturnTo />,
  },
  {
    path: ROUTES.DEVICE_MANAGEMENT,
    element: <Dashboard />,
  },
  {
    path: ROUTES.DEVICE_OVERVIEW,
    element: <Roadmap />,
  },
  { path: ROUTES.QMS, element: <QMS /> },
  { path: ROUTES.QMS_OPEN_DOC, element: <QMS /> },
  { path: ROUTES.QMS_OPEN_DOC_ID, element: <QMS /> },
  { path: ROUTES.TEMPLATE_ASSISTANT, element: <MultiStepForm /> },
  {
    path: ROUTES.CHECKOUT_SUCCESS,
    element: <CheckoutSuccess />,
  },
  {
    path: ROUTES.ASSISTANT,
    element: <Assistant />,
  },
  {
    path: ROUTES.ASSISTANT_DU_CONFIGURE,
    element: <DU_Configure />,
  },
  {
    path: ROUTES.ASSISTANT_DU_LIST_OF_DOCS_TO_CHANGE,
    element: <DU_ListOfDocsToChange />,
  },
  {
    path: ROUTES.ASSISTANT_DU_MULTI_STEP_FORM,
    element: <DU_MultiStepForm />,
  },
  {
    path: ROUTES.ASSISTANT_DU_GENERATING_DOCS,
    element: <LoadingNewDocs />,
  },
  {
    path: ROUTES.ASSISTANT_DU_UPDATE_DOCS,
    element: <DU_UpdateDocs />,
  },
];

const SaveLocationWrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { isAuthenticated } = useAuth0();

  useEffect(() => {
    if (!isAuthenticated) {
      const currentPath = `${location.pathname}${location.search}`;
      sessionStorage.setItem("returnTo", currentPath);
    }
  }, [location, isAuthenticated]);

  return <>{children}</>;
};

export const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <Auth0Provider
        domain={config.auth.issuerBaseUrl}
        clientId={config.auth.clientId}
        audience={config.auth.audience}
        redirectUri={window.location.origin + ROUTES.CALLBACK}
        cacheLocation="localstorage"
        useRefreshTokens={true}
      >
        <Outlet />
      </Auth0Provider>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <Navigate to={ROUTES.LOGIN} replace />,
      },
      {
        path: ROUTES.CALLBACK,
        element: <NavigateToHomeOrReturnTo />,
      },
      {
        path: ROUTES.LOGIN,
        element: <AuthPage type="login" />,
      },
      {
        path: ROUTES.SIGNUP,
        element: <AuthPage type="signup" />,
      },
      {
        path: ROUTES.HELP_CENTER,
        element: <HelpCenter />,
      },
      { path: ROUTES.HELP_CENTER_ARTICLE, element: <HelpCenterArticle /> },
      {
        path: ROUTES.DEACTIVATED_USER,
        element: <DeactivatedUserError />,
      },
      {
        path: `/${ROUTE_SLUGS.ORG_ID}`,
        element: (
          <SaveLocationWrapper>
            <MainLayout />
          </SaveLocationWrapper>
        ),
        children: [
          {
            path: "",
            element: <ProtectedRoute component={Outlet} />,
            children: GENERAL_ROUTES.map((route) => ({
              path: route.path,
              element: route.element,
              loader: route.loader || undefined,
            })),
          },
        ],
      },
    ],
  },
]);
